import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { BUILDER_CODE_ADDRESS, BUILDER_CODE_FEE } from '@/config/consts'
import { queryClient } from '@/config/query-client'
import { TokenWithBalance } from '@/domain/common/types'
import { useAccount } from '@/domain/hooks/useAccount'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { BaseUnitNumber, NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { GasLimitExceededDetails } from '@/features/swap/logic/constants'
import { Button } from '@/ui/atoms/button/Button'
import { toBigInt } from '@/utils/bigNumber'
import { normalizeUnsupportedCharacters } from '@/utils/strings'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import * as hl from '@nktkas/hyperliquid'
import { useState } from 'react'
import { erc20Abi, parseEther, parseUnits } from 'viem'
import { useChainId, usePublicClient, useWalletClient } from 'wagmi'
import { depositUSDC } from '../bridge/logic/depositUSDC'
import { swap } from '../swap/logic/swap'
import { GetPriceError, GetPriceResult } from '../swap/logic/types'
import { wrapUnwrap } from '../swap/logic/wrapUnrwap'
import { BridgeToken, TradeCurrentPage, getExchangeRateTokenId } from './logic/useTrade'
import { useWalletAgent } from './logic/useWalletAgent'
import { TradeObjective } from './types/trade'

const transport = new hl.HttpTransport()

const hlInfo = new hl.InfoClient({
  transport,
})

export function getTradeType(
  from: BridgeToken | TokenWithBalance,
  to: BridgeToken | TokenWithBalance,
  nativeAssetSymbol: TokenSymbol,
  wrappedNativeAssetSymbol: TokenSymbol,
  tradingSymbols: string[],
) {
  const defaultValues = {
    isWrap: false,
    isUnwrap: false,
    isDualL1Trade: false,
    isL1Trade: false,
    isEvmTrade: false,
    isTransferBefore: false,
    isTransferAfter: false,
    isTransfer: false,
  }

  const isTransfer = from.token.symbol === to?.token.symbol

  if (isTransfer) {
    return {
      ...defaultValues,
      isTransfer,
    }
  }

  const isWrap = from.token.symbol === nativeAssetSymbol && to?.token.symbol === wrappedNativeAssetSymbol
  const isUnwrap = from.token.symbol === wrappedNativeAssetSymbol && to?.token.symbol === nativeAssetSymbol

  if (isWrap || isUnwrap) {
    return {
      ...defaultValues,
      isWrap,
      isUnwrap,
      isTransferBefore: (from as BridgeToken)?.tokenId !== undefined,
      isTransferAfter: false,
    }
  }

  const isDualL1Trade =
    (from as BridgeToken)?.tokenId !== undefined &&
    (to as BridgeToken)?.tokenId !== undefined &&
    !tradingSymbols.includes(to?.token.symbol.toUpperCase()) &&
    !tradingSymbols.includes(from?.token.symbol.toUpperCase())

  if (isDualL1Trade) {
    return {
      ...defaultValues,
      isDualL1Trade,
    }
  }

  const isL1Trade =
    isDualL1Trade || ((from as BridgeToken)?.tokenId !== undefined && (to as BridgeToken)?.tokenId !== undefined)

  let isTransferBefore =
    (from as BridgeToken)?.tokenId === undefined &&
    (to as BridgeToken)?.tokenId !== undefined &&
    tradingSymbols.includes(to?.token.symbol.toUpperCase())
  let isTransferAfter =
    (to as BridgeToken)?.tokenId === undefined &&
    (from as BridgeToken)?.tokenId !== undefined &&
    tradingSymbols.includes(from?.token.symbol.toUpperCase())

  if (isTransferBefore || isTransferAfter || isL1Trade) {
    return {
      ...defaultValues,
      isWrap,
      isUnwrap,
      isL1Trade: isL1Trade || isTransferBefore || isTransferAfter,
      isEvmTrade: false,
      isTransferBefore,
      isTransferAfter,
      isTransfer,
    }
  }

  isTransferBefore = (from as BridgeToken)?.tokenId !== undefined && (to as BridgeToken)?.tokenId === undefined
  isTransferAfter = (from as BridgeToken)?.tokenId === undefined && (to as BridgeToken)?.tokenId !== undefined

  const isEvmTrade =
    ((from as BridgeToken)?.tokenId === undefined && (to as BridgeToken)?.tokenId === undefined) ||
    isTransferAfter ||
    isTransferBefore

  return {
    ...defaultValues,
    isWrap,
    isUnwrap,
    isL1Trade: false,
    isEvmTrade,
    isTransferBefore,
    isTransferAfter,
    isTransfer,
  }
}

function getButtonText(
  status: string,
  isWrap: boolean | undefined,
  isUnwrap: boolean | undefined,
  isTransfer: boolean | undefined,
  isTransferBefore: boolean | undefined,
  isTransferAfter: boolean | undefined,
  currentPage: TradeCurrentPage,
): string {
  if (status !== '') {
    return status
  }

  if (isWrap) {
    return 'Wrap'
  }

  if (isUnwrap) {
    return 'Unwrap'
  }

  if (isTransfer) {
    return 'Transfer'
  }

  let text = currentPage === 'trade' ? 'Trade' : 'Swap'

  if (isTransferBefore) {
    text = `Transfer & ${text}`
  }

  if (isTransferAfter) {
    text = `${text} & Transfer`
  }

  return text
}

type TwapOrderParameters = {
  hours: number
  minutes: number
  randomize: boolean
}

export default function TradeButton(props: {
  objectives: TradeObjective[]
  onFinish: () => void
  enabled: boolean
  exchangeRate: string
  setErrorMessage: (s: string) => void
  slippage: Percentage
  swapQuoteData: GetPriceResult | GetPriceError | null
  fromAssets: (BridgeToken | TokenWithBalance)[]
  toAssets: (BridgeToken | TokenWithBalance)[]
  tradingWithAssetsSymbols: string[]
  activeTab?: string
  limitPrice?: string
  twapParams?: TwapOrderParameters
  currentPage: TradeCurrentPage
  /** Whether the user is using big blocks (for gas limit check) */
  isUsingBigBlocks?: boolean
  /** Callback when gas limit would be exceeded */
  onGasLimitExceeded?: (details: GasLimitExceededDetails) => void
}) {
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()
  const chainId = useChainId()
  const nativeAssetInfo = getNativeAssetInfo(chainId)
  const address = useAccount()
  const [status, setStatus] = useState('')
  const { createAgentClient } = useWalletAgent(address)
  const { setErrorMessage, swapQuoteData, tradingWithAssetsSymbols } = props

  const objective = props.objectives[0]

  const walletConfig = {
    walletClient,
    publicClient,
    account: address,
  }

  const { isDualL1Trade, isWrap, isUnwrap, isL1Trade, isEvmTrade, isTransferBefore, isTransferAfter, isTransfer } =
    getTradeType(
      objective?.from || {
        token: new Token({
          symbol: TokenSymbol('-'),
          decimals: 6,
          address: CheckedAddress('0x0000000000000000000000000000000000000000'),
          unitPriceUsd: '0',
          name: '',
          isAToken: false,
        }),
        balance: NormalizedUnitNumber(0),
      },
      objective?.to || {
        token: new Token({
          symbol: TokenSymbol('-'),
          decimals: 6,
          address: CheckedAddress('0x0000000000000000000000000000000000000000'),
          unitPriceUsd: '0',
          name: '',
          isAToken: false,
        }),
        balance: NormalizedUnitNumber(0),
      },
      nativeAssetInfo.nativeAssetSymbol,
      nativeAssetInfo.wrappedNativeAssetSymbol,
      tradingWithAssetsSymbols,
    )

  async function handleEvmTrade(objective: TradeObjective): Promise<{ outputAmount?: string }> {
    // Convert slippage from Percentage to basis points (e.g., 1% = 100 BPS)
    const slippageBPS = Math.round(props.slippage.toNumber() * 10_000)

    return new Promise((resolve, reject) => {
      void swap({
        inputToken: objective!.from.token,
        outputToken: objective!.to!.token,
        inputAmount: objective!.from.token.toBaseUnit(objective!.amount),
        priceData: swapQuoteData,
        walletConfig,
        chainId,
        nativeAssetSymbol: nativeAssetInfo.nativeAssetSymbol,
        slippage: slippageBPS,
        isUsingBigBlocks: props.isUsingBigBlocks ?? false,
        setSwapState: (status) => setStatus(status),
        setSwapError: (isError, message) => {
          if (isError) {
            reject(new Error(message ?? 'Transaction failed'))
          }
        },
        setQuoteError: (isError, message) => {
          if (isError) {
            reject(new Error(message ?? 'Failed to get quote'))
          }
        },
        onSuccess: (data) => {
          resolve(data)
        },
        onGasLimitExceeded: (details) => {
          // When gas limit is exceeded, notify the parent and reject the promise
          // This stops the trade flow so the user can choose how to proceed
          if (props.onGasLimitExceeded) {
            props.onGasLimitExceeded(details)
          }
          reject('gas_limit_exceeded')
        },
      })
    })
  }

  async function handleWrapUnwrap(): Promise<void> {
    await new Promise((resolve, reject) => {
      void wrapUnwrap({
        walletConfig,
        chainId,
        value: toBigInt(objective!.from.token.toBaseUnit(objective!.amount)),
        swapMode: isWrap ? 'wrap' : 'unwrap',
        setSwapState: (status) => {
          setStatus(status)
        },
        setSwapError: (isError) => {
          if (isError) {
            reject('Transaction failed')
          }
        },
        setPageStatus: () => {
          resolve(undefined)
        },
      })
    })
  }

  async function handleTransfer(
    symbol: string,
    amount: string,
    direction: 'toEvm' | 'toCore',
    fromAssets: (BridgeToken | TokenWithBalance)[],
    toAssets: (BridgeToken | TokenWithBalance)[],
  ): Promise<void> {
    if (!address || !walletClient || !publicClient) {
      return
    }

    setStatus('Transferring...')

    try {
      if (direction === 'toEvm') {
        // Transfer from L1 to EVM
        await handleToEvmTransfer(walletClient, symbol, amount, fromAssets)
      } else {
        // Transfer from EVM to L1
        await handleToCoreTransfer(symbol, amount, toAssets)
      }

      // Wait 1 second before proceeding
      await new Promise((resolve) => setTimeout(resolve, 1000))
    } catch (error) {
      console.error(error)
      throw new Error(
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the request'
            : error.message
          : 'Transaction Failed',
      )
    } finally {
      setStatus('')
    }
  }

  async function handleToEvmTransfer(
    walletClient: any,
    symbol: string,
    amount: string,
    fromAssets: (BridgeToken | TokenWithBalance)[],
  ): Promise<void> {
    // Find tokenId and systemAddress from the from assets
    const fromAsset = fromAssets.find(
      (asset) => asset.token.symbol === symbol && (asset as BridgeToken).tokenId !== undefined,
    ) as BridgeToken

    if (!fromAsset || !fromAsset.tokenId || !fromAsset.systemAddress || !fromAsset.evmContract) {
      throw new Error(`Token ID or system address not found for: ${symbol}`)
    }

    const hlClient = new hl.ExchangeClient({
      wallet: walletClient,
      transport,
    })

    const tokenString = `${String(normalizeUnsupportedCharacters(symbol, { uppercase: true }))}:${fromAsset.tokenId}`

    const data = await hlClient.spotSend({
      destination: fromAsset.systemAddress as `0x${string}`,
      amount,
      token: tokenString as `${string}:0x${string}`,
    })

    if (data.status !== 'ok') {
      throw new Error(`Transfer failed: ${data.status}`)
    }
  }

  async function handleToCoreTransfer(
    symbol: string,
    amount: string,
    toAssets: (BridgeToken | TokenWithBalance)[],
  ): Promise<void> {
    if (!address || !publicClient || !walletClient) {
      throw new Error('Primary wallet is not available')
    }

    // Find systemAddress from the to assets
    const toAsset = toAssets.find(
      (asset) => asset.token.symbol === symbol && (asset as BridgeToken).tokenId !== undefined,
    ) as BridgeToken

    if (!toAsset || !toAsset.systemAddress || !toAsset.evmContract) {
      throw new Error(`System address not found for: ${symbol}`)
    }

    // Find the corresponding EVM token for the symbol
    const evmAsset = toAssets.find(
      (asset) => asset.token.symbol === symbol && (asset as BridgeToken).tokenId === undefined,
    )

    if (!evmAsset) {
      throw new Error(`EVM token not found for: ${symbol}`)
    }

    const tokenAddress = evmAsset.token.address as `0x${string}`

    // USDC requires a special approval -> deposit flow to bridge to HyperCore
    // depositContract contains the bridge contract address (evmContract is the real ERC20)
    if (symbol === 'USDC' && toAsset.depositContract) {
      const bridgeContractAddress = toAsset.depositContract as `0x${string}`
      const amountInBaseUnits = parseUnits(amount.toString(), evmAsset.token.decimals)

      await depositUSDC(walletClient, publicClient, tokenAddress, bridgeContractAddress, amountInBaseUnits)
      return
    }

    // For bridging to HyperCore, use wallet sendBalance
    const token =
      symbol === 'HYPE'
        ? undefined
        : {
            address: tokenAddress,
            decimals: evmAsset.token.decimals,
          }

    let tx: `0x${string}`

    if (token) {
      tx = await walletClient.writeContract({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'transfer',
        args: [toAsset.systemAddress as `0x${string}`, parseUnits(amount.toString(), token.decimals)],
      })
    } else {
      tx = await walletClient.sendTransaction({
        to: toAsset.systemAddress as `0x${string}`,
        value: parseEther(amount.toString()),
      })
    }

    await waitForTxReceipt(publicClient, tx)
  }

  async function handleL1Trade(
    objective: TradeObjective,
    slippage: Percentage,
    customPrice?: string,
  ): Promise<{ outputAmount?: string }> {
    if (!address || !walletClient || !publicClient) {
      throw new Error('Primary wallet or objective not found')
    }

    const exchangeToken = getExchangeRateTokenId(objective.from, objective.to!, props.fromAssets, props.toAssets)

    if (!exchangeToken?.tokenId) {
      throw new Error('Token ID not found')
    }

    const exchangeRate = queryClient.getQueryData<hl.TokenDetails>(['tokenDetails', exchangeToken.tokenId])

    setStatus('Trading...')

    try {
      const hlClient = new hl.ExchangeClient({
        wallet: walletClient,
        transport,
      })

      // 1 means 0.001%
      const builderFee = BUILDER_CODE_FEE.replace('%', '')
      const builderFeeInBps = Math.round(Number.parseFloat(builderFee) * 1000) // Convert percentage to basis points

      const maxBuilderFee = await hlInfo.maxBuilderFee({
        user: walletClient.account.address,
        builder: BUILDER_CODE_ADDRESS,
      })

      if (builderFeeInBps !== maxBuilderFee) {
        await hlClient.approveBuilderFee({
          maxFeeRate: BUILDER_CODE_FEE,
          builder: BUILDER_CODE_ADDRESS,
        })
      }

      const hlAgentClient = await createAgentClient(walletClient)

      // isBuy = true when selling USDC to buy the exchange token, false when selling the exchange token for USDC
      const isBuy = objective.from.token.symbol.toUpperCase() !== exchangeToken.token.symbol.toUpperCase()

      const data = await hlAgentClient.order({
        orders: [
          {
            a: exchangeToken.universeIndex, // assetId
            b: isBuy,
            p: (() => {
              // Otherwise use market price with slippage
              const slippageValue = Number(slippage)

              // Count decimals in original exchange rate
              const originalDecimals = exchangeRate?.markPx?.includes('.')
                ? (exchangeRate.markPx.split('.')[1]?.length || 1) - 1
                : 0

              let adjustedRate: number

              if (isBuy) {
                // Increase price for buy
                if (customPrice) {
                  adjustedRate = Number(1 / Number(customPrice))
                } else {
                  adjustedRate = Number(exchangeRate?.markPx) * (1 + slippageValue)
                }
              } else {
                // Decrease price for sell
                if (customPrice) {
                  adjustedRate = Number(customPrice)
                } else {
                  adjustedRate = Number(exchangeRate?.markPx) * (1 - slippageValue)
                }
              }

              // Apply same decimal precision as original
              return adjustedRate.toFixed(originalDecimals)
            })(),
            s: isBuy
              ? Number(
                  (Number(objective.amount) * Number(customPrice ? customPrice : props.exchangeRate)).toFixed(
                    exchangeToken.szDecimals,
                  ),
                ).toString()
              : Number(Number(objective.amount).toFixed(exchangeToken.szDecimals)).toString(), // size
            r: false, // isReduceOnly
            t: { limit: { tif: customPrice ? 'Gtc' : 'FrontendMarket' } }, // Use GTC for limit orders, FrontendMarket for market orders
          },
        ],
        grouping: 'na',
        builder: {
          b: BUILDER_CODE_ADDRESS,
          f: builderFeeInBps,
        },
      })

      // Wait 1 second before proceeding
      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (data.status === 'ok') {
        const status = data?.response?.data?.statuses[0]
        const outputAmount = status && 'filled' in status ? status.filled.totalSz : undefined

        return { outputAmount }
      }

      throw new Error(`Transfer Failed: ${data.status}`)
    } catch (error) {
      console.error(error)

      throw new Error(
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the request'
            : error.message
          : 'Transaction Failed',
      )
    } finally {
      setStatus('')
    }
  }

  async function handleTWAPTrade(
    objective: TradeObjective,
    twapParams: TwapOrderParameters,
  ): Promise<{ outputAmount?: string }> {
    if (!address || !walletClient || !publicClient) {
      throw new Error('Primary wallet or objective not found')
    }

    const exchangeToken = getExchangeRateTokenId(objective.from, objective.to!, props.fromAssets, props.toAssets)

    if (!exchangeToken?.tokenId) {
      throw new Error('Token ID not found')
    }

    setStatus('Creating TWAP order...')

    try {
      const hlClient = new hl.ExchangeClient({
        wallet: walletClient,
        transport,
      })

      // 1 means 0.001%
      const builderFee = BUILDER_CODE_FEE.replace('%', '')
      const builderFeeInBps = Math.round(Number.parseFloat(builderFee) * 1000) // Convert percentage to basis points

      const maxBuilderFee = await hlInfo.maxBuilderFee({
        user: walletClient.account.address,
        builder: BUILDER_CODE_ADDRESS,
      })

      if (builderFeeInBps !== maxBuilderFee) {
        await hlClient.approveBuilderFee({
          maxFeeRate: BUILDER_CODE_FEE,
          builder: BUILDER_CODE_ADDRESS,
        })
      }

      const hlAgentClient = await createAgentClient(walletClient)

      // isBuy = true when selling USDC to buy the exchange token, false when selling the exchange token for USDC
      const isBuy = objective.from.token.symbol.toUpperCase() !== exchangeToken.token.symbol.toUpperCase()

      const data = await hlAgentClient.twapOrder({
        a: exchangeToken.universeIndex, // assetId
        b: isBuy,
        s: isBuy
          ? Number((Number(objective.amount) * Number(props.exchangeRate)).toFixed(exchangeToken.szDecimals)).toString()
          : Number(Number(objective.amount).toFixed(exchangeToken.szDecimals)).toString(), // size
        m: twapParams.hours * 60 + twapParams.minutes, // runtime in minutes
        r: false, // isReduceOnly
        t: twapParams.randomize,
      })

      // Wait 1 second before proceeding
      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (data.status === 'ok') {
        return { outputAmount: undefined } // TWAP orders don't have immediate output
      }

      throw new Error(`TWAP Order Failed: ${data.status}`)
    } catch (error) {
      console.error(error)

      throw new Error(
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the request'
            : error.message
          : 'TWAP Order Failed',
      )
    } finally {
      setStatus('')
    }
  }

  return (
    <div className="relative pb-1">
      <Button
        className="w-full rounded-lg"
        disabled={!props.enabled || status !== '' || (isTransferAfter && !(isEvmTrade || isL1Trade))}
        onClick={async () => {
          try {
            const objective = props.objectives[0]

            if (!objective) {
              setErrorMessage('No objective available')
              return
            }

            setErrorMessage('')

            if (isDualL1Trade) {
              const usdcL1Token = props.fromAssets.find(
                (asset) => asset.token.symbol === 'USDC' && (asset as BridgeToken).tokenId !== undefined,
              ) as BridgeToken

              const { outputAmount } = await handleL1Trade(
                { ...objective, to: usdcL1Token },
                Percentage(Number(props.slippage) / 2),
                props.activeTab === 'Limit' ? props.limitPrice : undefined,
              )

              if (outputAmount) {
                objective.amount = NormalizedUnitNumber(outputAmount)

                await handleL1Trade(
                  { ...objective, from: usdcL1Token },
                  Percentage(Number(props.slippage) / 2),
                  props.activeTab === 'Limit' ? props.limitPrice : undefined,
                )
              }

              props.onFinish()
              return
            }

            if (isTransfer) {
              const direction = (objective.from as BridgeToken).tokenId !== undefined ? 'toEvm' : 'toCore'

              await handleTransfer(
                objective.from.token.symbol,
                objective.amount.toString(),
                direction,
                props.fromAssets,
                props.toAssets,
              )
              props.onFinish()

              return
            }

            if (isTransferBefore) {
              const direction = (objective.from as BridgeToken).tokenId !== undefined ? 'toEvm' : 'toCore'

              await handleTransfer(
                objective.from.token.symbol,
                objective.amount.toString(),
                direction,
                props.fromAssets,
                props.toAssets,
              )

              objective.from = props.fromAssets.find(
                (asset) =>
                  asset.token.symbol === objective.from!.token.symbol &&
                  (asset as BridgeToken).tokenId !== (objective.from as BridgeToken).tokenId,
              ) as BridgeToken
            }

            if (isWrap) {
              await handleWrapUnwrap()

              props.onFinish()

              return
            }

            if (isUnwrap) {
              await handleWrapUnwrap()

              if (!isTransferAfter) {
                props.onFinish()

                return
              }
            }

            if (isEvmTrade) {
              const { outputAmount } = await handleEvmTrade(objective)

              if (!isTransferAfter) {
                props.onFinish()
                return
              }

              if (outputAmount) {
                objective.from = props.fromAssets.find(
                  (asset) =>
                    asset.token.symbol === objective.to!.token.symbol && (asset as BridgeToken).tokenId === undefined,
                ) as BridgeToken

                objective.amount = objective.from!.token.fromBaseUnit(BaseUnitNumber(outputAmount))
              }
            } else if (isL1Trade) {
              if (props.activeTab === 'TWAP' && props.twapParams) {
                await handleTWAPTrade(objective, props.twapParams)
                props.onFinish()
                return
              }

              const { outputAmount } = await handleL1Trade(
                objective,
                props.slippage,
                props.activeTab === 'Limit' ? props.limitPrice : undefined,
              )

              if (!isTransferAfter) {
                props.onFinish()
                return
              }

              if (outputAmount) {
                objective.from = props.fromAssets.find(
                  (asset) =>
                    asset.token.symbol === objective.to!.token.symbol && (asset as BridgeToken).tokenId !== undefined,
                ) as BridgeToken

                objective.amount = NormalizedUnitNumber(outputAmount)
              }
            }

            if (isTransferAfter) {
              const direction = (objective.from as BridgeToken).tokenId !== undefined ? 'toEvm' : 'toCore'

              await handleTransfer(
                objective.from.token.symbol,
                objective.amount.toString(),
                direction,
                props.fromAssets,
                props.toAssets,
              )
              props.onFinish()

              return
            }
          } catch (error) {
            // Don't show error message for gas_limit_exceeded - the modal handles that case
            if (error === 'gas_limit_exceeded') {
              return
            }
            console.error(error)

            setErrorMessage(error instanceof Error ? error.message : 'Transaction Failed')
          } finally {
            setStatus('')
          }
        }}
      >
        {getButtonText(status, isWrap, isUnwrap, isTransfer, isTransferBefore, isTransferAfter, props.currentPage)}
      </Button>
    </div>
  )
}
